import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const credentialsSchema = z.object({
  site: z
    .string()
    .trim()
    .regex(/^(https?:\/\/)?[a-z0-9][a-z0-9-]*(\.trial)?\.ezyvet\.com\/?$/i, "Enter your ezyVet address, e.g. yourclinic.ezyvet.com"),
  client_id: z.string().trim().min(1, "Client ID required").max(256),
  client_secret: z.string().trim().min(1, "Client secret required").max(512),
  partner_id: z.string().trim().max(128).optional(),
  environment: z.enum(["production", "trial"]),
  site_uid: z.string().trim().max(128).optional(),
  scope: z.string().trim().max(2000).optional(),
});

export type CredentialsValues = z.infer<typeof credentialsSchema>;

interface Props {
  busy: boolean;
  reconnecting: boolean;
  onSubmit: (v: CredentialsValues) => void;
  onCancel?: () => void;
}

/**
 * ezyVet OAuth client credentials. Write-only: VetGuard checks them with
 * ezyVet before storing them encrypted, and never sends them back.
 */
export function CredentialsForm({ busy, reconnecting, onSubmit, onCancel }: Props) {
  const [advanced, setAdvanced] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { site: "", client_id: "", client_secret: "", partner_id: "", environment: "production" },
  });

  return (
    <form className="card" onSubmit={handleSubmit(onSubmit)} autoComplete="off" aria-labelledby="creds-title">
      <h2 id="creds-title">{reconnecting ? "Update ezyVet credentials" : "Connect ezyVet"}</h2>
      <p className="muted">
        Ask ezyVet support to issue API credentials for VetGuard with read access to consults, invoices and clinical
        records (and write access to tasks if you want flags pushed back). VetGuard checks them with ezyVet before
        saving and stores them encrypted; they are never shown again.
      </p>

      <div className="field">
        <label htmlFor="site">ezyVet address</label>
        <input id="site" type="text" placeholder="yourclinic.ezyvet.com" spellCheck={false} {...register("site")} />
        {errors.site && <span className="field-error">{errors.site.message}</span>}
      </div>

      <div className="form-grid">
        <div className="field">
          <label htmlFor="client_id">Client ID</label>
          <input id="client_id" type="text" spellCheck={false} className="mono" {...register("client_id")} />
          {errors.client_id && <span className="field-error">{errors.client_id.message}</span>}
        </div>
        <div className="field">
          <label htmlFor="client_secret">Client secret</label>
          <div className="row" style={{ gap: 8 }}>
            <input
              id="client_secret"
              type={showSecret ? "text" : "password"}
              autoComplete="new-password"
              spellCheck={false}
              className="mono grow"
              {...register("client_secret")}
            />
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setShowSecret((s) => !s)}
              aria-pressed={showSecret}
              aria-controls="client_secret"
            >
              {showSecret ? "Hide" : "Show"}
            </button>
          </div>
          {errors.client_secret && <span className="field-error">{errors.client_secret.message}</span>}
        </div>
        <div className="field">
          <label htmlFor="partner_id">Partner ID</label>
          <input id="partner_id" type="text" spellCheck={false} className="mono" placeholder="Optional" {...register("partner_id")} />
          {errors.partner_id && <span className="field-error">{errors.partner_id.message}</span>}
        </div>
        <div className="field">
          <label htmlFor="environment">Environment</label>
          <select id="environment" {...register("environment")}>
            <option value="production">Production</option>
            <option value="trial">Trial (sandbox)</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAdvanced((a) => !a)} aria-expanded={advanced}>
          {advanced ? "▾ Hide" : "▸ Show"} advanced fields
        </button>
      </div>
      {advanced && (
        <>
          <p className="muted">Only if ezyVet support gave you these values.</p>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="site_uid">Site UID</label>
              <input id="site_uid" type="text" className="mono" {...register("site_uid")} />
            </div>
            <div className="field">
              <label htmlFor="scope">Scope</label>
              <input id="scope" type="text" className="mono" {...register("scope")} />
            </div>
          </div>
        </>
      )}

      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Checking with ezyVet…" : reconnecting ? "Save credentials" : "Connect"}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
